import { z } from 'zod';
import { cleanString } from './clean.js';

// Per-client contract merge fields (T-041) — entered once per client and
// reused on every contract. All nullable/optional; admins fill them on the
// client edit page. Name fields are cleanString()-normalized (T-200); the
// multi-line description/location/address fields are left verbatim.
const ContractMergeFields = {
  contract_client_name: cleanString(z.string().min(1).max(200)).nullable().optional(),
  contract_description_of_work: z.string().min(1).max(2000).nullable().optional(),
  contract_work_location: z.string().min(1).max(400).nullable().optional(),
  contract_work_site_address: z.string().min(1).max(1000).nullable().optional(),
};

export const ClientCreateInput = z.object({
  name: cleanString(z.string().min(1).max(100)),
  legal_name: cleanString(z.string().min(1).max(200)),
  project_or_product_name: cleanString(z.string().min(1).max(200)),
  ...ContractMergeFields,
  // Whether this client's engagements are emitted in a billing run (T-059).
  // Defaults off so a new client can't accidentally bill until opted in.
  billing_in_scope: z.boolean().optional(),
  active: z.boolean().optional(),
});
export type ClientCreateInput = z.infer<typeof ClientCreateInput>;

export const ClientUpdateInput = z
  .object({
    name: cleanString(z.string().min(1).max(100)).optional(),
    legal_name: cleanString(z.string().min(1).max(200)).optional(),
    project_or_product_name: cleanString(z.string().min(1).max(200)).optional(),
    ...ContractMergeFields,
    billing_in_scope: z.boolean().optional(),
    active: z.boolean().optional(),
  })
  .refine((obj) => Object.keys(obj).length > 0, {
    message: 'At least one field must be provided',
  });
export type ClientUpdateInput = z.infer<typeof ClientUpdateInput>;

export const Client = z.object({
  id: z.string().uuid(),
  name: z.string(),
  legal_name: z.string(),
  project_or_product_name: z.string(),
  contract_client_name: z.string().nullable(),
  contract_description_of_work: z.string().nullable(),
  contract_work_location: z.string().nullable(),
  contract_work_site_address: z.string().nullable(),
  billing_in_scope: z.boolean(),
  active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Client = z.infer<typeof Client>;

/**
 * The four per-client contract fields are a one-time setup entry that must be
 * complete before a contractor can be onboarded under the client (T-044). The
 * single source of truth for "complete" — shared by the onboarding picker, the
 * onboarding-create server guard, the clients-list badge, and the contract-send
 * backstop. Accepts any object carrying the four columns (DB row or Client).
 */
export type ClientContractFields = Pick<
  Client,
  | 'contract_client_name'
  | 'contract_description_of_work'
  | 'contract_work_location'
  | 'contract_work_site_address'
>;

export function isClientContractComplete(client: ClientContractFields): boolean {
  return (
    !!client.contract_client_name?.trim() &&
    !!client.contract_description_of_work?.trim() &&
    !!client.contract_work_location?.trim() &&
    !!client.contract_work_site_address?.trim()
  );
}
